import type { SessionType } from '../types';

interface SessionProcessingCopy {
  title: string;
  lines: string[];
}

const COPY_BY_TYPE: Record<SessionType | 'default', SessionProcessingCopy> = {
  morning_checkin: {
    title: 'Reflecting on your check-in',
    lines: [
      'Noticing what stood out this morning and what it might mean for your day.',
      'Distilling the signal in this check-in so tomorrow starts even sharper.',
      'Learning your patterns from this check-in so support keeps getting more personal.',
    ],
  },
  evening_checkin: {
    title: 'Reflecting on your day',
    lines: [
      'Distilling today into useful signal and a little wisdom for tomorrow.',
      'Noticing what shifted today so your next steps can be clearer.',
      'Learning from this reflection to make future coaching more tailored.',
    ],
  },
  task_planning: {
    title: 'Shaping your task plan',
    lines: [
      'Turning your ideas into clear, doable next steps.',
      'Tidying up the plan so it is easier to act on quickly.',
      'Capturing your priorities so the task stays grounded and focused.',
    ],
  },
  coaching: {
    title: 'Reflecting on your coaching call',
    lines: [
      'Capturing the key thread from this conversation so we can build on it next time.',
      'Distilling what clicked in this call and where to lean in next.',
      'Learning from this coaching moment to make future sessions smarter.',
    ],
  },
  onboarding: {
    title: 'Getting to know you better',
    lines: [
      'Reflecting on your onboarding conversation to personalize what comes next.',
      'Distilling your priorities and patterns to tailor your coaching experience.',
      'Learning your style so future sessions feel more relevant from the start.',
    ],
  },
  ad_hoc: {
    title: 'Reflecting on your conversation',
    lines: [
      'Pulling out the useful signal from this conversation.',
      'Distilling what mattered most so future support stays relevant.',
      'Learning from this session to keep coaching aligned with you.',
    ],
  },
  default: {
    title: 'Reflecting on your conversation',
    lines: [
      'Distilling what mattered most from this session.',
      'Learning from this conversation to support you better next time.',
      'Capturing useful signal from this session — almost done.',
    ],
  },
};

export function getSessionProcessingCopy(sessionType: SessionType | null): SessionProcessingCopy {
  if (!sessionType) return COPY_BY_TYPE.default;
  return COPY_BY_TYPE[sessionType] ?? COPY_BY_TYPE.default;
}

function stableIndex(seed: string, size: number): number {
  if (size <= 1) return 0;
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) ^ seed.charCodeAt(i);
  }
  return Math.abs(hash) % size;
}

export function getSessionProcessingLine(sessionType: SessionType | null, sessionId: string | null): string {
  const copy = getSessionProcessingCopy(sessionType);
  if (copy.lines.length === 0) return '';
  const seed = sessionId ?? sessionType ?? 'default';
  return copy.lines[stableIndex(seed, copy.lines.length)];
}
